import { Card } from 'antd';
import React from 'react';
import { connect } from 'react-redux';
import { ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import {
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
	safeJSONParseOrElse,
} from '../../utils/DataAccessUtils';
import { UserName } from '../name_component/user/UserNameComponent';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { locationAudits } from '../../thunks/locations_thunks';
import AuditChangeComponent from '../common/audit/AuditChangeComponent';
import { getAuditDiffForJsonValue } from '../common/audit/auditDiffUtils';
import OWPaginatedTimeline from '../common/OWPaginatedTimeline';
import { withUserType } from '../withUserType';

const fieldMap = {
	name: 'Name',
	isActive: 'Is Active',
	description: 'Description',
	buyerFacilityId: 'Buyer Facility',
	images: 'Images',
	operatingHours: 'Operating Hours',
	phoneNumber: 'Phone Number',
	currencyId: 'Currency',
	storeId: 'Store',
	brandId: 'Brand',
	regionId: 'Region',
	facilityInChargeEmail: 'Facility In Charge Emails',
	facilityInChargeEmails: 'Facility In Charge Emails',
	sendApprovedInvoicesToEmails: 'Send Approved Invoices To Emails',
	primaryContactsEmails: 'Primary Contacts Emails',
	locationTypeId: 'Location Type',
	locationTypeDetails: 'Location Type Details',
	notes: 'Notes',
	internalNotes: 'Internal Notes',
	internalAttachments: 'Internal Attachments',
	isLeased: 'Is Leased',
	leaseStartDate: 'Lease Start Date',
	leaseEndDate: 'Lease End Date',
	buildingWarrantyStartDate: 'Building Warranty Start Date',
	buildingWarrantyExpirationDate: 'Building Warranty End Date',
	buildingWarrantyServiceProviderId: 'Building Warranty Service Provider',
	openDate: 'Open Date',
	closeDate: 'Close Date',
	fieldTechEmails: 'Field Tech Emails',
	storeAssociateEmails: 'Store Associate Emails',
};

const getActiveValue = (field, key) =>
	nullSafeGetOrElse(key, field, 'false') === 'true' ? 'Active' : 'Inactive';

const LocationAudit = ({ match, locationAudits, location }) => {
	const locationId = match.params.id;

	const getDescription = (item) => {
		const getFieldsChangeCopy = (field) => {
			switch (field.fieldName) {
				case 'regionId':
					return (
						<AuditChangeComponent
							resolvedFieldName={fieldMap[field.fieldName]}
							fieldName={field.fieldName}
							resolvedOldValue={nullSafeGet('fromRegion.name', item)}
							oldValue={field.oldValue}
							resolvedNewValue={nullSafeGet('toRegion.name', item)}
							newValue={field.newValue}
						/>
					);
				case 'brandId':
					return (
						<AuditChangeComponent
							resolvedFieldName={fieldMap[field.fieldName]}
							fieldName={field.fieldName}
							resolvedOldValue={nullSafeGet('fromBrand.brandName', item)}
							oldValue={field.oldValue}
							resolvedNewValue={nullSafeGet('toBrand.brandName', item)}
							newValue={field.newValue}
						/>
					);
				case 'locationTypeId':
					return (
						<AuditChangeComponent
							resolvedFieldName={fieldMap[field.fieldName]}
							fieldName={field.fieldName}
							resolvedOldValue={nullSafeGet('fromLocationType.name', item)}
							oldValue={field.oldValue}
							resolvedNewValue={nullSafeGet('toLocationType.name', item)}
							newValue={field.newValue}
						/>
					);
				case 'buildingWarrantyServiceProviderId':
					return (
						<AuditChangeComponent
							resolvedFieldName={fieldMap[field.fieldName]}
							fieldName={field.fieldName}
							resolvedOldValue={nullSafeGet('fromBuildingWarrantyServiceProvider.name', item)}
							oldValue={field.oldValue}
							resolvedNewValue={nullSafeGet('toBuildingWarrantyServiceProvider.name', item)}
							newValue={field.newValue}
						/>
					);
				case 'locationTypeDetails':
					return getAuditDiffForJsonValue(field).map((changeProp, idx) => (
						<AuditChangeComponent key={`locationTypeDetails-change-${idx}`} {...changeProp} />
					));
				case 'isActive':
					return (
						<AuditChangeComponent
							resolvedFieldName={fieldMap[field.fieldName]}
							fieldName={field.fieldName}
							resolvedOldValue={getActiveValue(field, 'oldValue')}
							oldValue={field.oldValue}
							resolvedNewValue={getActiveValue(field, 'newValue')}
							newValue={field.newValue}
						/>
					);
				case 'fieldTechEmails':
				case 'facilityInChargeEmails':
				case 'sendApprovedInvoicesToEmails':
				case 'primaryContactsEmails':
				case 'facilityInChargeEmail':
				case 'storeAssociateEmails':
					const oldEmails = safeJSONParseOrElse(
						field.oldValue,
						(field.oldValue || '').split(', ').map((email) => email.trim())
					);
					const newEmails = safeJSONParseOrElse(
						field.newValue,
						(field.newValue || '').split(', ').map((email) => email.trim())
					);

					const addedEmails = newEmails.filter((email) => !oldEmails.includes(email));
					const removedEmails = oldEmails.filter((email) => !newEmails.includes(email));

					return (
						<span className="inline-block rounded-md bg-violet-100 p-2 text-sm text-gray-500">
							{fieldMap[field.fieldName] || field.fieldName} Changed :
							<div>
								{(addedEmails.length && (
									<div className="mt-1 inline-block rounded-md bg-violet-50 p-1 text-sm text-gray-500">
										<i>Added :</i> {addedEmails.join(', ')}{' '}
									</div>
								)) ||
									null}

								{(removedEmails.length && (
									<div className="mt-1 inline-block rounded-md bg-violet-50 p-1 text-sm text-gray-500">
										<i>Removed :</i> {removedEmails.join(', ')}{' '}
									</div>
								)) ||
									null}
							</div>
						</span>
					);
				case 'leaseStartDate':
				case 'leaseEndDate':
				case 'openDate':
				case 'closeDate':
				case 'buildingWarrantyStartDate':
				case 'buildingWarrantyExpirationDate':
					return (
						<span className="inline-block rounded-md bg-violet-100 p-2 text-sm text-gray-500">
							{fieldMap[field.fieldName] || field.fieldName} :{' '}
							{(field.oldValue && (
								<DateTimeHover
									timeZone={nullSafeGet('timeZoneId', location)}
									showTime={false}
									showDate
									timestamp={field.oldValue}
								/>
							)) ||
								`' '`}{' '}
							<ArrowRightOutlined />{' '}
							{(field.newValue && (
								<DateTimeHover
									timeZone={nullSafeGet('timeZoneId', location)}
									showTime={false}
									showDate
									timestamp={field.newValue}
								/>
							)) ||
								`' '`}
						</span>
					);

				default:
					return (
						<span className="inline-block rounded-md bg-violet-100 p-2 text-sm text-gray-500">
							{fieldMap[field.fieldName] || field.fieldName} : {field.oldValue || `' '`}{' '}
							<ArrowRightOutlined /> {field.newValue}
						</span>
					);
			}
		};

		const getFieldsChange = (item) => {
			return (
				(item.fieldValueChanges && (
					<div className="mt-3 flex flex-wrap gap-2">
						{item.fieldValueChanges.map((field) =>
							field.fieldName === 'internalNotes' || field.fieldName === 'internalAttachments' ? (
								<AccessPermissionChecker name={PERMISSION_NAMES.SHOW_INTERNAL_TECH_NOTES}>
									{getFieldsChangeCopy(field)}
								</AccessPermissionChecker>
							) : (
								getFieldsChangeCopy(field)
							)
						)}
					</div>
				)) ||
				null
			);
		};

		return (
			<div>
				Edited by <UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
				{getFieldsChange(item)}
			</div>
		);
	};
	const getIcon = (item) => {
		return <EditOutlined style={{ color: '#000000' }} />;
	};
	return (
		<div className="flex flex-col p-4">
			<Card>
				<div className="flex">
					<OWPaginatedTimeline
						fetchData={locationAudits}
						renderRecord={(item) => (
							<div className="mb-5 pt-1">
								<div className=" text-base ">{getDescription(item)}</div>
								<span className="mt-3 inline-block text-sm text-gray-500">
									<DateTimeHover
										timestamp={nullSafeGet('createdAt', item)}
										showDate={true}
										timeZone={nullSafeGet('timeZoneId', location)}
									/>
								</span>
							</div>
						)}
						getDot={getIcon}
						additionalParams={{ locationId }}
					/>
				</div>
			</Card>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	userType: state.session.userType,
	location: getEntityById(ownProps.match.params.id, state.locations),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	locationAudits: (params) => dispatch(locationAudits(ownProps.userType)(params)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(LocationAudit));
